import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Building2, Users, UserCheck, MoreHorizontal, Edit2, Trash2, Save, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import StatusBadge from "@/components/common/StatusBadge";
import UserCell from "@/components/common/UserCell";
import Can, { useCan } from "@/components/common/Can";
import Forbidden from "@/pages/Forbidden";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const inputCls = "h-11 w-full rounded-xl border border-border bg-background px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40";

function StatMini({ Icon, label, value, accent }) {
    return (
        <div className="card-elevated p-4">
            <div className="flex items-center gap-3">
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${accent}`}>
                    <Icon className="h-5 w-5" />
                </div>
                <div>
                    <div className="text-[11.5px] text-muted-foreground">{label}</div>
                    <div className="text-[20px] font-bold leading-tight tracking-tight">{value}</div>
                </div>
            </div>
        </div>
    );
}

function DepartmentDialog({ open, onOpenChange, department, employees, onSaved }) {
    const [form, setForm] = useState({ name: "", description: "", manager_id: "", status: "active" });
    const [saving, setSaving] = useState(false);
    const editing = !!department;

    useEffect(() => {
        if (open) {
            setForm(department ? {
                name: department.name || "",
                description: department.description || "",
                manager_id: department.manager_id || "",
                status: department.status || "active",
            } : { name: "", description: "", manager_id: "", status: "active" });
        }
    }, [open, department]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return toast.error(tr.validation.required);
        setSaving(true);
        try {
            const payload = { ...form, manager_id: form.manager_id || null };
            if (editing) {
                await api.put(`/departments/${department.id}`, payload);
                toast.success("Departman güncellendi.");
            } else {
                await api.post("/departments", payload);
                toast.success("Departman oluşturuldu.");
            }
            onSaved();
            onOpenChange(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Kaydedilemedi.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg rounded-2xl">
                <DialogHeader>
                    <DialogTitle>{editing ? "Departmanı Düzenle" : "Yeni Departman"}</DialogTitle>
                    <DialogDescription>Departman bilgilerini girin.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 pt-2" data-testid="department-form">
                    <div className="space-y-1.5">
                        <label className="block text-[12.5px] font-semibold">{tr.common.name}</label>
                        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="dept-name" />
                    </div>
                    <div className="space-y-1.5">
                        <label className="block text-[12.5px] font-semibold">{tr.common.description}</label>
                        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full rounded-xl border border-border bg-background px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <label className="block text-[12.5px] font-semibold">{tr.common.manager}</label>
                            <select value={form.manager_id} onChange={(e) => setForm({ ...form, manager_id: e.target.value })} className={inputCls} data-testid="dept-manager">
                                <option value="">{tr.departments.noManager}</option>
                                {employees.map((e) => (
                                    <option key={e.id} value={e.id}>{e.full_name} ({e.title || e.role})</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-1.5">
                            <label className="block text-[12.5px] font-semibold">{tr.common.status}</label>
                            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                                <option value="active">{tr.status.active}</option>
                                <option value="inactive">{tr.status.inactive}</option>
                            </select>
                        </div>
                    </div>
                    <DialogFooter>
                        <button type="button" onClick={() => onOpenChange(false)} className="inline-flex items-center justify-center rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold hover:bg-muted">
                            {tr.common.cancel}
                        </button>
                        <button type="submit" disabled={saving} className="btn-brand" data-testid="dept-save">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            {tr.common.save}
                        </button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default function DepartmentsList() {
    const canView = useCan("departments.view");
    const canCreate = useCan("departments.create");
    const canEdit = useCan("departments.edit");
    const canDelete = useCan("departments.delete");

    const [depts, setDepts] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState({ open: false, department: null });
    const [confirmDel, setConfirmDel] = useState({ open: false, dept: null });
    const [search, setSearch] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [d, e] = await Promise.all([
                api.get("/departments", { params: search ? { search } : undefined }),
                api.get("/employees", { params: { page_size: 200 } }),
            ]);
            setDepts(d.data.items || []);
            setEmployees(e.data.items || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => { if (canView) load(); }, [load, canView]);

    if (!canView) return <Forbidden />;

    const active = depts.filter((d) => d.status === "active").length;
    const withMgr = depts.filter((d) => d.manager).length;

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-[26px] font-bold tracking-tight">{tr.departments.title}</h1>
                    <p className="text-[13.5px] text-muted-foreground mt-1">{tr.departments.subtitle}</p>
                </div>
                {canCreate && (
                    <button onClick={() => setDialog({ open: true, department: null })} className="btn-brand h-11" data-testid="btn-new-department">
                        <Plus className="h-4 w-4" /> {tr.departments.new}
                    </button>
                )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <StatMini Icon={Building2} label={tr.departments.totalDepartments} value={depts.length} accent="bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))]" />
                <StatMini Icon={UserCheck} label={tr.departments.activeDepartments} value={active} accent="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" />
                <StatMini Icon={Users} label={tr.departments.totalManagers} value={withMgr} accent="bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]" />
            </div>

            {/* Search */}
            <div className="card-elevated p-4 flex items-center gap-3">
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Departman ara…"
                    className="h-10 flex-1 rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
                    data-testid="dept-search"
                />
                {search && (
                    <button onClick={() => setSearch("")} className="inline-flex h-10 items-center justify-center rounded-lg border border-border px-3 hover:bg-muted">
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>

            {/* Grid */}
            {loading ? (
                <div className="py-16 text-center text-muted-foreground">{tr.common.loading}</div>
            ) : depts.length === 0 ? (
                <div className="card-elevated p-10 text-center text-muted-foreground">{tr.common.noResults}</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {depts.map((d) => (
                        <div key={d.id} className="card-elevated p-5 group hover:shadow-card-hover transition-all">
                            <div className="flex items-start justify-between mb-3">
                                <Link to={`/departments/${d.id}`} className="flex items-center gap-3 min-w-0">
                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))]">
                                        <Building2 className="h-5 w-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-[14.5px] font-semibold truncate">{d.name}</div>
                                        <div className="text-[11.5px] text-muted-foreground">{d.employee_count} personel</div>
                                    </div>
                                </Link>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted" aria-label="İşlemler" data-testid={`dept-actions-${d.id}`}>
                                            <MoreHorizontal className="h-4 w-4" />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-40">
                                        {canEdit && (
                                            <DropdownMenuItem onClick={() => setDialog({ open: true, department: d })}>
                                                <Edit2 className="mr-2 h-4 w-4" /> {tr.common.edit}
                                            </DropdownMenuItem>
                                        )}
                                        {canDelete && (
                                            <DropdownMenuItem
                                                onClick={() => setConfirmDel({ open: true, dept: d })}
                                                className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]"
                                            >
                                                <Trash2 className="mr-2 h-4 w-4" /> {tr.common.delete}
                                            </DropdownMenuItem>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                            {d.description && <p className="text-[12.5px] text-muted-foreground line-clamp-2 mb-3">{d.description}</p>}
                            <div className="flex items-center justify-between pt-3 border-t border-border">
                                {d.manager ? (
                                    <UserCell name={d.manager.full_name} subtitle={d.manager.title || tr.common.manager} avatarUrl={d.manager.avatar_url} size="sm" />
                                ) : (
                                    <div className="text-[11.5px] text-muted-foreground">{tr.departments.noManager}</div>
                                )}
                                <StatusBadge status={d.status || "active"} />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <DepartmentDialog
                open={dialog.open}
                onOpenChange={(o) => setDialog({ ...dialog, open: o })}
                department={dialog.department}
                employees={employees}
                onSaved={load}
            />

            <ConfirmDialog
                open={confirmDel.open}
                onOpenChange={(o) => setConfirmDel({ ...confirmDel, open: o })}
                title={tr.departments.confirmDelete}
                description={tr.departments.confirmDeleteBody}
                confirmText={tr.common.yesDelete}
                variant="danger"
                onConfirm={async () => {
                    try {
                        await api.delete(`/departments/${confirmDel.dept.id}`);
                        toast.success("Departman silindi.");
                        setConfirmDel({ open: false, dept: null });
                        load();
                    } catch (err) {
                        toast.error(err?.response?.data?.detail || "Silinemedi.");
                    }
                }}
            />
        </div>
    );
}
